import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, initializeAuth, browserLocalPersistence, browserPopupRedirectResolver, connectAuthEmulator, setPersistence, type Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore, initializeFirestore, persistentLocalCache, persistentSingleTabManager } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

export type FirebaseClient = {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  storage: FirebaseStorage;
  functions: Functions;
};

// Configuración de Firebase (usando variables de entorno de Vite)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validación de configuración
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error('❌ Firebase config is missing or incomplete:', firebaseConfig);
  console.error('Available env vars:', import.meta.env);
  // No lanzamos error para evitar pantalla en blanco total. 
  // Las apps manejarán el estado sin auth o fallarán de forma más controlada.
}

console.log('✅ Firebase config loaded:', {
  apiKey: firebaseConfig.apiKey ? '***' : 'MISSING',
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain
});

// Inicialización Singleton
export const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
console.log('✅ Firebase app initialized:', app.name);

// Inicializar servicios de Firebase de forma lazy
// Auth se inicializa solo cuando se necesita, no al cargar el módulo
let authInstance: Auth | null = null;

export const getAuthInstance = (): Auth => {
  if (!authInstance) {
    try {
      // Intentar inicializar con persistencia explícita y popup resolver
      authInstance = initializeAuth(app, {
        persistence: browserLocalPersistence,
        popupRedirectResolver: browserPopupRedirectResolver
      });
    } catch (e) {
      // Si falla (ya estaba inicializada), obtener la instancia existente
      authInstance = getAuth(app);
      // Forzar persistencia para sesiones futuras
      setPersistence(authInstance, browserLocalPersistence).catch(err =>
        console.error("⚠️ Error enforcing persistence:", err)
      );
    }
    console.log('✅ Firebase Auth initialized');
  }
  return authInstance;
};

// Para compatibilidad con código existente, exportar un getter
export const auth = new Proxy({} as Auth, {
  get(target, prop) {
    return getAuthInstance()[prop as keyof Auth];
  }
});
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentSingleTabManager(undefined)
  })
});
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Conectar emuladores solo en desarrollo local y si la flag está activa
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATOR === 'true') {
  console.log("🔥 [Shared] Conectando a emuladores de Firebase");
  try {
    // Evitar conectar dos veces si ya están conectados (aunque Firebase suele manejarlo, es mejor prevenir)
    // Nota: No hay método oficial 'isConnected', así que confiamos en que este código corre una vez.
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectAuthEmulator(auth, 'http://localhost:9099');
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
  } catch (e) {
    console.warn("⚠️ [Shared] Error conectando emuladores (¿ya conectados?):", e);
  }
}

export function initFirebase(): FirebaseClient {
  return { app, auth, db, storage, functions };
}
